/* =============================================================================
   VIEWS / ADMIN / SETTINGS-TEAM — Team-Sub-Tab (S49 / Phase 40)
   =============================================================================
   S66 Refactor: aus settings.js extrahiert (Zeilen 2275-2807, ~533 LOC).

   Owner/Admin sieht alle Tenant-User, kann einladen / Rolle ändern / (de)
   aktivieren. Voll abgesichert über die Migration-40-RPCs (list_team_users /
   create_team_user / update_team_user_role / set_team_user_active).

   Memory-Lessons:
     - feedback_gg_db_for_critical_data — DB ist Single-Source, keine Cache-Layer
     - feedback_gg_hard_way               — RPCs mit auth_user_id-Pattern
     - feedback_gg_non_destructive_render — Edit-Aktionen rendern nur die Liste neu

   S133: Aktiv/Inaktiv-Split — Deaktivierte wandern in eine eingeklappte
   Soft-Delete-Sektion unten (reduzierte Zeilen, nur Reaktivieren); die
   Änderungs-Historie ist eingeklappt + lädt den Audit-Trail lazy beim ersten
   Aufklappen. Aufklapp-Zustand übersteht den non-destruktiven Re-Render
   (Module-State inactiveOpen/auditOpen, teamToggleSection).

   Test-Hooks (alle mit Unterstrich-Präfix): _isUiAssignableRole,
   _isValidTeamEmail, _findSelfUser, _callerCanEditTeam, _sortTeamUsers,
   _splitTeamUsers, _renderRoleSelectHtml, _renderLinkedBadge,
   _renderInactiveSection, _buildTeamAuditRowHtml, _formatTeamAuditAction,
   _formatTeamAuditDetails.

   S262 Cut A: Spalte „Dienstplan" je User (Toggle → set_team_user_schedule_
   participant, owner/admin/manager, auditiert) + Karte „Dienstplan-Zyklus"
   (settings.schedule über den Merge-Pfad). Hooks: _callerCanToggleSchedule,
   _renderScheduleCell, _renderDowSelectHtml.

   Dependencies aus settings.js werden via window-Wrapper aufgelöst (kein Circular).
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
// S126 — PIN-Vergabe über das stylebare PIN-Pad-Modal (kein window.prompt mehr).
import { openSetPinModal } from './operator-ui.js';
// S222 Cut 2 — Rollen-Idle-Timeouts (pure Helper für Default/Clamp).
import { roleIdleTimeoutMinutes } from '../../core/operator-roles.js';
// S262 Cut A — Dienstplan: Defaults/Klemmen des Settings-Slots + Wochentage.
import { normalizeScheduleSettings, WEEKDAY_LONG } from '../../core/schedule-shifts.js';

// -----------------------------------------------------------------------------
// Cross-Module Helpers (Anti-Circular)
// -----------------------------------------------------------------------------
function _renderSettings() {
  if (typeof window !== 'undefined' && typeof window.renderSettings === 'function') {
    window.renderSettings();
  }
}

// -----------------------------------------------------------------------------
// Team-Sub-Tab (Session 49, Phase 40) — User-Management
// -----------------------------------------------------------------------------
// Owner/Admin sieht alle Tenant-User, kann einladen / Rolle ändern /
// (de)aktivieren. Voll abgesichert über die Migration-40-RPCs
// (list_team_users / create_team_user / update_team_user_role /
// set_team_user_active). DB ist Single-Source — keine Cache-Schichten,
// Memory-Lessons feedback_gg_db_for_critical_data + feedback_gg_hard_way.
// Non-destructive Re-Render (Memory feedback_gg_non_destructive_render):
// nach Edit-Aktionen nur die Liste neuzeichnen, nicht den ganzen Settings-
// Tab — Focus auf Inputs bleibt erhalten.
// -----------------------------------------------------------------------------

// S131 Clean-Cut: service (Kellner) + staff (Küche/Theke/Lieferung), host raus.
// S209: driver (Fahrer) — löst den alten drivers-Stammdaten-Pfad ab.
export const TEAM_UI_ROLES = ['admin', 'manager', 'service', 'staff', 'lager', 'driver'];
export const TEAM_ROLE_LABELS = {
  owner:   'Owner',
  admin:   'Admin',
  manager: 'Manager',
  service: 'Service',
  staff:   'Staff',
  lager:   'Lager',
  driver:  'Fahrer'
};
export const TEAM_ROLE_DESCRIPTIONS = {
  admin:   'Voller Zugriff inkl. Team-Verwaltung und Einstellungen.',
  manager: 'Heute, Operativ, Ware, Auswertung, Tischplan/Speisekarte; System-Einstellungen nur lesend; Storno, Z-Bon, Fahrer.',
  service: 'Bedient die Gäste: aufnehmen, Status, kassieren, Reservierungen, Walk-Ins.',
  staff:   'Küche/Theke/Lieferung: bedient die Displays (Status, Fahrer zuweisen).',
  // S167 — reine Warenwirtschaft: sieht NUR den Ware-Tab (Lager, Bestellungen,
  // Schwund, Chargen) und darf dort buchen. Keine Gäste/Kasse/Auswertung/System.
  lager:   'Warenwirtschaft: nur Ware-Tab (Lager, Bestellungen, Schwund, Chargen) inkl. Buchungen.',
  // S209 — Fahrer: mobile Terminal-View mit den eigenen Lieferungen
  // (Unterwegs → Zugestellt, Anruf + Navigation). Keine Admin-Tabs.
  driver:  'Fahrer: mobiles Liefer-Terminal (eigene Lieferungen, Unterwegs → Zugestellt, Anruf + Navigation). Keine Admin-Ansichten.'
};

// Module-State — wird durch refreshTeamCard async gefüllt.
let _teamState = {
  loading:    true,
  error:      null,
  users:      [],
  inviteName: '',
  inviteEmail: '',
  invitePhone: '',
  inviteRole: 'service',
  // Audit-View (Phase 42 / S52): nur für owner geladen.
  // S133: lazy — erst beim ersten Aufklappen der Historie-Sektion.
  audit:       [],
  auditError:  null,
  auditLoaded: false,
  // S133: Aufklapp-Zustand der <details>-Sektionen — übersteht den
  // non-destruktiven innerHTML-Re-Render (analog Focus-State).
  inactiveOpen: false,
  auditOpen:    false
};

/** Pure: Klassifiziert eine Rolle. Wird in Tests genutzt. */
export function _isUiAssignableRole(role) {
  return TEAM_UI_ROLES.indexOf(role) !== -1;
}

/** Pure: Email-Format-Check, identisch zur Server-Validation. */
export function _isValidTeamEmail(email) {
  if (typeof email !== 'string') return false;
  const t = email.trim();
  if (!t) return false;
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(t);
}

/** Pure: Lokaler Caller-Self-Check anhand der geladenen Users-Liste. Erkennt
 *  den eingeloggten User über is_self-Flag aus der RPC. */
export function _findSelfUser(users) {
  if (!Array.isArray(users)) return null;
  return users.find(u => u && u.is_self === true) || null;
}

/** Pure: Bestimmt ob der eingeloggte User Team-Editier-Rechte hat
 *  (Caller-Rolle ∈ {owner, admin}). */
export function _callerCanEditTeam(users) {
  const self = _findSelfUser(users);
  return !!self && (self.role === 'owner' || self.role === 'admin');
}

/** Pure (S262): der Dienstplan-Schalter ist auch für den Manager bedienbar
 *  (Spec § 6.3: owner/admin/manager) — Server-Gate in
 *  set_team_user_schedule_participant identisch. */
export function _callerCanToggleSchedule(users) {
  const self = _findSelfUser(users);
  return !!self && (self.role === 'owner' || self.role === 'admin' || self.role === 'manager');
}

/** Pure (S262): Title-Text des Dienstplan-Toggles je Zustand (K1: wird beim
 *  non-destruktiven Patch mitgezogen). */
export function _scheduleToggleTitle(on) {
  return on ? 'nimmt am Dienstplan teil — Klick zum Ausschalten' : 'nicht im Dienstplan — Klick zum Einschalten';
}

/** Pure (S262): Zelle „Dienstplan" — Toggle (bedienbar) oder Anzeige-Text.
 *  B1 (S263): der Toggle liegt in `.settings-ro-exempt`, sonst schaltet ihn
 *  `_applySettingsDisablePass` im Nur-Lese-Modus des Managers tot — obwohl die
 *  RPC den Manager ausdrücklich zulässt (Muster DSGVO-Aktion im Compliance-Tab). */
export function _renderScheduleCell(u, canToggle) {
  const on = !!(u && u.schedule_participant);
  if (!canToggle) {
    return `<td class="team-schedule-cell">${on ? '🗓️ ja' : '<span class="hint">nein</span>'}</td>`;
  }
  const id = htmlEscape(u.id);
  return `
    <td class="team-schedule-cell">
      <div class="settings-ro-exempt">
        <label class="toggle" title="${_scheduleToggleTitle(on)}">
          <input type="checkbox" ${on ? 'checked' : ''} data-schedule-for="${id}"
                 onchange="toggleTeamUserSchedule('${id}', this)">
          <span class="toggle-slider"></span>
        </label>
      </div>
    </td>`;
}

/** Pure (S133): trennt aktive von deaktivierten Usern (Soft-Delete-Liste).
 *  Fehlende active-Flags zählen als aktiv (defensiv, RPC liefert boolean). */
export function _splitTeamUsers(users) {
  const list = Array.isArray(users) ? users : [];
  return {
    active:   list.filter(u => u && u.active !== false),
    inactive: list.filter(u => u && u.active === false)
  };
}

/** Pure: Tabellen-Sort identisch zur RPC, falls UI ohne Reload neusortiert. */
export function _sortTeamUsers(users) {
  const order = { owner: 1, admin: 2, manager: 3, service: 4, staff: 5, lager: 6, driver: 7 };
  return [...(users || [])].sort((a, b) => {
    const oa = order[a.role] || 99;
    const ob = order[b.role] || 99;
    if (oa !== ob) return oa - ob;
    return (a.name || '').localeCompare(b.name || '', 'de');
  });
}

export function renderTeamSection() {
  // Erste Render-Stufe rendert nur das Skeleton; refreshTeamCard() füllt async.
  // S222 Cut 2 — die Rollen-Timeout-Karte hängt AUSSERHALB von #team-card, damit
  // sie der non-destruktive Team-Card-Re-Render (_renderTeamCardInner) nicht
  // überschreibt.
  return `
    <div class="card" id="team-card">
      <h2>Team-Mitglieder &amp; Berechtigungen</h2>
      <p class="hint" style="margin-bottom: 14px;">
        Lade Team-Liste…
      </p>
    </div>
    ${renderOperatorTimeoutsCard()}
    ${renderScheduleCycleCard()}
  `;
}

// -----------------------------------------------------------------------------
// S262 Cut A — Karte „Dienstplan-Zyklus": An/Aus + 3× Wochentag + Uhrzeit.
// settings.schedule = { enabled, requestDow, requestTime, reminderDow,
// reminderTime, deadlineDow, deadlineTime } über den jsonb-Merge-Pfad
// (updateSetting → update_restaurant_settings, Server gated owner/admin).
// Owner/Admin/Manager sehen die Karte; der Manager bekommt sie über den
// Read-Only-Modus des Settings-Tabs deaktiviert (_disableSettingsInputs +
// updateSetting-Guard) — wie der Rest von System.
// Schreib-Serialisierung wie bei den Rollen-Timeouts (S223-HF2): zwei schnelle
// Feld-Commits dürfen sich das Slot-Objekt nicht gegenseitig überschreiben.
// -----------------------------------------------------------------------------

/** Owner/Admin/Manager-Gate über das Auth-Konto. */
function _authIsOwnerAdminManager() {
  try {
    const u = window.App && window.App.auth && typeof window.App.auth.getCurrentUserSync === 'function'
      ? window.App.auth.getCurrentUserSync() : null;
    return !!u && (u.role === 'owner' || u.role === 'admin' || u.role === 'manager');
  } catch (_) { return false; }
}

/** Aktueller settings.schedule-Slot, normalisiert (Defaults + Klemmen). */
function _scheduleSlot() {
  try {
    const st = window.App && window.App.storage && typeof window.App.storage._getInternalState === 'function'
      ? window.App.storage._getInternalState() : null;
    return normalizeScheduleSettings(st && st.settings && st.settings.schedule);
  } catch (_) { return normalizeScheduleSettings(null); }
}

/** Pure: Wochentag-Select (1 = Montag … 7 = Sonntag). */
export function _renderDowSelectHtml(field, current) {
  const opts = WEEKDAY_LONG.map((label, i) => {
    const v = i + 1;
    return `<option value="${v}" ${v === Number(current) ? 'selected' : ''}>${htmlEscape(label)}</option>`;
  }).join('');
  return `<select data-schedule-field="${htmlEscape(field)}" onchange="updateScheduleCycleField('${htmlEscape(field)}', this.value)">${opts}</select>`;
}

export function renderScheduleCycleCard() {
  if (!_authIsOwnerAdminManager()) return '';
  const s = _scheduleSlot();
  const row = (label, dowField, timeField, hint) => `
    <div class="schedule-cycle-row">
      <label>${htmlEscape(label)}</label>
      ${_renderDowSelectHtml(dowField, s[dowField])}
      <input type="time" value="${htmlEscape(s[timeField])}" step="60"
             data-schedule-field="${htmlEscape(timeField)}"
             onchange="updateScheduleCycleField('${htmlEscape(timeField)}', this.value)">
      <span class="hint">${htmlEscape(hint)}</span>
    </div>`;
  return `
    <div class="card" id="schedule-cycle-card" style="margin-top: var(--space-4);">
      <h2>Dienstplan-Zyklus</h2>
      <p class="hint" style="margin-bottom:12px;">
        Alle Team-Mitglieder mit eingeschaltetem Dienstplan-Schalter bekommen automatisch
        eine Mail mit ihrem persönlichen Link und tragen ihre Verfügbarkeit für die
        Folgewoche (Mo–So) ein. Wer nichts einträgt, bekommt keine Schichten.
        Zeiten in der Restaurant-Zeitzone.
      </p>
      <div class="toggle-row" style="margin-bottom:12px;">
        <label class="toggle" title="${s.enabled ? 'Zyklus läuft — Klick zum Ausschalten' : 'Zyklus aus — Klick zum Einschalten'}">
          <input type="checkbox" ${s.enabled ? 'checked' : ''} data-schedule-field="enabled"
                 onchange="updateScheduleCycleField('enabled', this.checked)">
          <span class="toggle-slider"></span>
        </label>
        <span style="margin-left:10px;">Automatischer Zyklus ${s.enabled ? 'an' : 'aus'}</span>
      </div>
      ${row('Abfrage senden',   'requestDow',  'requestTime',  'Mail mit Link an alle Teilnehmer')}
      ${row('Erinnerung',       'reminderDow', 'reminderTime', 'nur an die, die noch nichts eingetragen haben')}
      ${row('Abgabefrist',      'deadlineDow', 'deadlineTime', 'danach ist der Link gesperrt')}
    </div>
  `;
}

let _scheduleWriteChain = Promise.resolve();

/** Handler (CSP func()): ein Feld des Zyklus setzen und den GANZEN Slot
 *  zurückschreiben (jsonb-Merge). Serialisiert, kein Re-Render — der Wert
 *  steht schon im Feld. Ungültiges wird über normalizeScheduleSettings
 *  auf den Default gezogen und im Feld sichtbar zurückgesetzt. */
export function updateScheduleCycleField(field, rawValue) {
  const allowed = ['enabled', 'requestDow', 'requestTime', 'reminderDow', 'reminderTime', 'deadlineDow', 'deadlineTime'];
  if (allowed.indexOf(field) === -1) return _scheduleWriteChain;
  _scheduleWriteChain = _scheduleWriteChain.then(async () => {
    const next = { ..._scheduleSlot() };
    next[field] = (field === 'enabled') ? (rawValue === true || rawValue === 'true') : rawValue;
    const clean = normalizeScheduleSettings(next);
    // Sichtbares Zurücksetzen, falls der Wert nicht durchkam (z. B. leeres time-Feld).
    if (String(clean[field]) !== String(next[field])) {
      const el = document.querySelector(`[data-schedule-field="${field}"]`);
      if (el) {
        if (el.type === 'checkbox') el.checked = !!clean[field];
        else el.value = String(clean[field]);
      }
    }
    if (typeof window.updateSetting === 'function') {
      await window.updateSetting('schedule', clean);
    }
    if (field === 'enabled') {
      const lbl = document.querySelector('#schedule-cycle-card .toggle-row span');
      if (lbl) lbl.textContent = 'Automatischer Zyklus ' + (clean.enabled ? 'an' : 'aus');
    }
  }).catch(e => { console.warn('[team] updateScheduleCycleField:', e); });
  return _scheduleWriteChain;
}

/** S262 Action: Dienstplan-Teilnahme eines Mitglieds umschalten
 *  (set_team_user_schedule_participant, auditiert). Non-destruktiv: der Toggle
 *  steht schon richtig, nur der Modul-State zieht nach; bei Fehler wird die
 *  Checkbox zurückgestellt. Kein Re-Render der Liste (Focus/Scroll bleiben). */
export async function toggleTeamUserSchedule(userId, checkboxEl) {
  const on = !!(checkboxEl && checkboxEl.checked);
  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { error } = await supa.rpc('set_team_user_schedule_participant', { p_user_id: userId, p_on: on });
    if (error) throw error;
    const u = (_teamState.users || []).find(x => x && x.id === userId);
    if (u) u.schedule_participant = on;
    // K1 (S263): der Title der Zelle zieht beim Patch mit — bis zum nächsten
    // Card-Render stand sonst der alte Text.
    const lbl = (checkboxEl && checkboxEl.closest) ? checkboxEl.closest('label.toggle') : null;
    if (lbl) lbl.title = _scheduleToggleTitle(on);
    showToast(on ? 'Im Dienstplan: bekommt die Abfrage-Mail.' : 'Aus dem Dienstplan genommen.', 'success');
  } catch (err) {
    console.error('[team] toggleTeamUserSchedule:', err);
    if (checkboxEl) checkboxEl.checked = !on;
    showToast('Dienstplan-Schalter fehlgeschlagen: ' + (err.message || err), 'error');
  }
}

// -----------------------------------------------------------------------------
// S222 Cut 2 — Automatische Operator-Sperre bei Inaktivität, PRO ROLLE.
// settings.operatorRoleTimeouts = { owner:15, …, driver:0 } (Minuten, 0=aus).
// Owner/Admin-only (gleiche Sicht wie „Dieses Gerät"-Karte). onchange (ein Write
// pro Feld-Commit, S223-HF2) + Write-Serialisierung, kein Re-Render (Focus bleibt).
// CSP: Handler als func() ohne window.
// -----------------------------------------------------------------------------

// Reihenfolge der Rollen in der Karte (owner…driver).
const ROLE_TIMEOUT_ORDER = ['owner', 'admin', 'manager', 'service', 'staff', 'lager', 'driver'];

/** Owner/Admin-Gate über das Auth-Konto (analog renderDeviceSettingsCard). */
function _authIsOwnerAdmin() {
  try {
    const u = window.App && window.App.auth && typeof window.App.auth.getCurrentUserSync === 'function'
      ? window.App.auth.getCurrentUserSync() : null;
    return !!u && (u.role === 'owner' || u.role === 'admin');
  } catch (_) { return false; }
}

/** Aktueller settings.operatorRoleTimeouts-Slot ({} wenn nie gesetzt). */
function _roleTimeoutSlot() {
  try {
    const st = window.App && window.App.storage && typeof window.App.storage._getInternalState === 'function'
      ? window.App.storage._getInternalState() : null;
    return (st && st.settings && st.settings.operatorRoleTimeouts) || {};
  } catch (_) { return {}; }
}

export function renderOperatorTimeoutsCard() {
  if (!_authIsOwnerAdmin()) return '';
  const slot = _roleTimeoutSlot();
  const rows = ROLE_TIMEOUT_ORDER.map(r => {
    const val = roleIdleTimeoutMinutes(r, slot);
    return `
      <div class="field-row" style="gap:10px; align-items:center; margin-bottom:8px;">
        <label style="flex:0 0 130px; font-size:.85rem;">${htmlEscape(TEAM_ROLE_LABELS[r] || r)}</label>
        <input type="number" min="0" max="240" step="1" value="${val}"
               data-role-timeout="${htmlEscape(r)}"
               onchange="updateOperatorRoleTimeout('${htmlEscape(r)}', this.value)"
               style="width:90px;">
        <span class="hint" style="font-size:.78rem;">Minuten · 0 = aus</span>
      </div>`;
  }).join('');
  // Eingeklappte Liste (Jonas 2026-07-05): die Zeiten stehen selten zur Debatte —
  // Karte + Hinweis sichtbar, die Zahlen-Felder bei Bedarf aufklappen. Die Karte
  // liegt AUSSERHALB von #team-card und rendert nach dem Öffnen nicht neu →
  // der <details>-open-Zustand hält von allein (kein teamToggleSection nötig).
  return `
    <div class="card" id="operator-timeouts-card" style="margin-top: var(--space-4);">
      <h2>Automatische Operator-Sperre bei Inaktivität</h2>
      <p class="hint" style="margin-bottom:12px;">
        Pro Rolle: nach wie vielen Minuten ohne Eingabe der angemeldete Operator
        automatisch abgemeldet und das Gerät gesperrt wird (erneute PIN-Anmeldung
        nötig). <strong>0 = aus</strong> (nie automatisch sperren). Gilt geräte-
        übergreifend für jeden Operator dieser Rolle. Ein Fahrer läuft standardmäßig
        auf 0 — Handy-Standby zwischen Stopps ist Normal-Betrieb.
      </p>
      <details class="team-collapse">
        <summary>Zeiten pro Rolle anzeigen &amp; anpassen</summary>
        <div style="margin-top:10px;">
          ${rows}
        </div>
      </details>
    </div>
  `;
}

// S223-HF2 — Write-Serialisierung: Rollen-Timeout-Writes laufen strikt
// nacheinander (Promise-Chain). Sonst können zwei überlappende
// updateSetting('operatorRoleTimeouts', …)-PATCHes das ganze Objekt schreiben
// und der stale Write gewinnt das DB-Race (live reproduziert: „30" getippt →
// DB owner=3, weil der „3"-Write als Letzter ankam). Jeder verkettete Write liest
// den Slot ERST beim Ausführen frisch aus dem State (updateSetting stempelt den
// State synchron) → immer der aktuellste Merge.
let _roleTimeoutWriteChain = Promise.resolve();

/** Handler (window-exposed, CSP func()): einen Rollen-Timeout clampen (0–240) und
 *  das GANZE operatorRoleTimeouts-Objekt zurückschreiben (JSONB-Merge-Konsistenz).
 *  Serialisiert (S223-HF2) + kein Re-Render (Wert steht schon im Input). Danach
 *  Idle-Watch re-syncen, damit ein neuer Wert der aktuell angemeldeten Rolle sofort
 *  greift. Feuert per `onchange` (ein Write pro Feld-Commit, nicht pro Tastendruck). */
export function updateOperatorRoleTimeout(role, rawValue) {
  if (ROLE_TIMEOUT_ORDER.indexOf(role) === -1) return _roleTimeoutWriteChain;
  let n = parseInt(rawValue, 10);
  if (!Number.isFinite(n) || n < 0) n = 0;
  if (n > 240) n = 240;
  _roleTimeoutWriteChain = _roleTimeoutWriteChain.then(async () => {
    const slot = { ...(_roleTimeoutSlot() || {}) };
    slot[role] = n;
    if (typeof window.updateSetting === 'function') {
      await window.updateSetting('operatorRoleTimeouts', slot);
    }
    try {
      if (typeof window.operatorResyncIdleWatch === 'function') window.operatorResyncIdleWatch();
    } catch (_) { /* ignore */ }
  }).catch(e => { console.warn('[team] updateOperatorRoleTimeout:', e); });
  return _roleTimeoutWriteChain;
}

/** Liest die aktuelle Liste über die RPC und re-rendert die Card. */
export async function refreshTeamCard() {
  // S133 Scroll-Fix: Skeleton nur beim Erst-Load. Bei bereits geladener Liste
  // bleibt der alte Inhalt bis zur frischen Antwort stehen — kein Höhen-Kollaps,
  // kein Scroll-Sprung nach Team-Aktionen (Jonas-Befund S133-UI-Test).
  const firstLoad = !Array.isArray(_teamState.users) || _teamState.users.length === 0;
  _teamState.loading = firstLoad;
  _teamState.error   = null;
  if (firstLoad) _renderTeamCardInner();

  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { data, error } = await supa.rpc('list_team_users');
    if (error) throw error;
    _teamState.users = Array.isArray(data) ? data : [];
    _teamState.loading = false;
  } catch (err) {
    console.error('[team] list_team_users:', err);
    _teamState.error = err && (err.message || String(err));
    _teamState.loading = false;
    _teamState.users   = [];
  }
  _renderTeamCardInner();

  // Phase 42 / S52 → S133: Audit-View lazy. Initial wird NICHT geladen
  // (erst beim ersten Aufklappen der Historie, teamToggleSection). War die
  // Historie aber schon geladen/offen, halten wir sie nach Team-Aktionen
  // frisch (neuer Eintrag soll sofort sichtbar sein).
  const self = _findSelfUser(_teamState.users);
  if (self && self.role === 'owner' && _teamState.auditLoaded) {
    refreshTeamAuditCard();
  }
}

/** S133: Toggle-Handler der <details>-Sektionen (Inline-onclick auf <summary>).
 *  Hält den open-State im Module-State, damit der non-destruktive Re-Render
 *  den Zustand reproduziert. Erstes Aufklappen der Historie lädt das Audit
 *  lazy nach (Owner-only — die RPC gate't selbst). */
export function teamToggleSection(key) {
  if (key === 'inactive') {
    _teamState.inactiveOpen = !_teamState.inactiveOpen;
    return;
  }
  if (key === 'audit') {
    _teamState.auditOpen = !_teamState.auditOpen;
    if (_teamState.auditOpen && !_teamState.auditLoaded) {
      refreshTeamAuditCard();
    }
  }
}

/**
 * Phase 42 / S52: lädt User-Lifecycle-Audit über RPC list_team_user_audit.
 * Owner-only. Aktualisiert dann den Audit-Bereich der Team-Card non-destruktiv.
 */
export async function refreshTeamAuditCard() {
  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { data, error } = await supa.rpc('list_team_user_audit', { p_limit: 20 });
    if (error) throw error;
    _teamState.audit       = Array.isArray(data) ? data : [];
    _teamState.auditError  = null;
    _teamState.auditLoaded = true;
  } catch (err) {
    console.error('[team] list_team_user_audit:', err);
    _teamState.audit       = [];
    _teamState.auditError  = err && (err.message || String(err));
    _teamState.auditLoaded = true;
  }
  _renderTeamCardInner();
}

/** Non-destructive Re-Render NUR der Team-Card. Erhält Focus + Cursor-Position
 *  von Form-Inputs (Memory feedback_gg_non_destructive_render). */
export function _renderTeamCardInner() {
  const card = document.getElementById('team-card');
  if (!card) return;

  // Focus-State sichern
  const active = document.activeElement;
  let focusKey = null;
  let focusCaret = null;
  if (active && card.contains(active) && active.tagName === 'INPUT' && active.dataset.teamFocusKey) {
    focusKey   = active.dataset.teamFocusKey;
    focusCaret = (active.selectionEnd != null) ? active.selectionEnd : null;
  }

  // S133: Scroll-Position über den innerHTML-Swap retten (analog Focus-State).
  const scrollX = (typeof window !== 'undefined') ? window.scrollX : 0;
  const scrollY = (typeof window !== 'undefined') ? window.scrollY : 0;

  card.innerHTML = _teamCardInnerHtml();

  if (typeof window !== 'undefined' && (window.scrollX !== scrollX || window.scrollY !== scrollY)) {
    window.scrollTo(scrollX, scrollY);
  }

  // Focus wiederherstellen
  if (focusKey) {
    const el = card.querySelector(`input[data-team-focus-key="${focusKey}"]`);
    if (el) {
      el.focus();
      try {
        if (focusCaret != null && (el.type === 'text' || el.type === 'email')) {
          el.setSelectionRange(focusCaret, focusCaret);
        }
      } catch (_) { /* ignore */ }
    }
  }
}

/** Pure: HTML für eine Rollen-Select-Dropdown.
 *  excludeOwner=true filtert 'owner' aus den auswählbaren Optionen raus
 *  (UI-Vergabe verboten). ABER: wenn der Target-User aktuell owner ist,
 *  zeigen wir 'owner' trotzdem als disabled-Option im Dropdown — sonst
 *  würde der Browser das erste Element ('admin') als selected rendern und
 *  die UI würde fälschlicherweise behaupten, der Owner sei ein Admin
 *  (S49-Bug-Befund Jonas). */
export function _renderRoleSelectHtml(currentRole, excludeOwner, attrs) {
  // S131 Clean-Cut: admin/manager/service/staff UI-vergebbar; owner nur disabled
  // sichtbar, falls Target-User Owner ist (sonst rendert der Browser fälschlich
  // die erste Option als selected).
  const opts = ['admin', 'manager', 'service', 'staff'];
  const all  = excludeOwner ? opts : ['owner', ...opts];
  if (currentRole === 'owner' && !all.includes('owner')) all.unshift('owner');
  // Unbekannte/legacy Rolle (z.B. Altbestand) sichtbar halten, aber gesperrt.
  if (currentRole && !all.includes(currentRole)) all.push(currentRole);
  return `<select ${attrs || ''}>
    ${all.map(r => `<option value="${r}" ${r === currentRole ? 'selected' : ''}${(r === 'owner' || !TEAM_ROLE_LABELS[r] || r === 'host') ? ' disabled' : ''}>${TEAM_ROLE_LABELS[r] || r}</option>`).join('')}
  </select>`;
}

/** S167 Multi-Role: Checkbox-Gruppe für die vergebbaren Rollen eines Users.
 *  Mehrere Rollen gleichzeitig möglich; mind. eine muss aktiv bleiben (der
 *  Handler revertiert sonst). owner ist nicht über die UI vergebbar. */
export function _renderRoleCheckboxesHtml(currentRoles, userId) {
  const have = new Set(Array.isArray(currentRoles) ? currentRoles : []);
  const assignable = ['admin', 'manager', 'service', 'staff', 'lager', 'driver'];
  const chips = assignable.map(r => `
    <label class="team-role-chip${have.has(r) ? ' is-on' : ''}">
      <input type="checkbox" value="${r}" ${have.has(r) ? 'checked' : ''}
        onchange="changeTeamUserRoles('${htmlEscape(userId)}', this)">
      ${htmlEscape(TEAM_ROLE_LABELS[r] || r)}
    </label>`).join('');
  return `<div class="team-role-checks" data-user="${htmlEscape(userId)}">${chips}</div>`;
}

/** Pure (S133): Login-Verknüpfungs-Badge (Magic-Link-Status). */
export function _renderLinkedBadge(u) {
  return u && u.auth_user_id
    ? `<span class="badge-soft badge-soft-info" title="Magic-Link-Login bereits genutzt">verknüpft</span>`
    : `<span class="badge-soft badge-soft-warn" title="Wartet auf ersten Magic-Link-Login">eingeladen</span>`;
}

/** S133: Name+E-Mail-Zelle. S209: Telefon wandert UNTER die Mail (gibt den
 *  Rollen mehr Breite). `editablePhone` = true rendert das inline-editierbare
 *  Feld (aktive Liste, owner/admin); sonst nur Anzeige-Text falls vorhanden. */
function _nameCellHtml(u, editablePhone) {
  const phoneRow = editablePhone
    ? _renderPhoneCell(u)
    : (u.phone ? `<div class="hint team-phone">📞 ${htmlEscape(u.phone)}</div>` : '');
  return `
    <td>
      <div class="team-name">${htmlEscape(u.name || '—')}${u.is_self ? ' <span class="hint team-self-hint">(du)</span>' : ''}</div>
      <div class="hint team-email">${htmlEscape(u.email || '')}</div>
      ${phoneRow}
    </td>`;
}

/** S133: Eingeklappte Soft-Delete-Sektion „Deaktivierte Mitglieder".
 *  Reduzierte Zeilen: Name/E-Mail, Rolle als Text, Login-Badge, Reaktivieren
 *  (nur canEdit). Kein Rollen-Select, keine PIN/Initialen — wer etwas ändern
 *  will, reaktiviert zuerst. Rendert bei 0 Inaktiven gar nichts. */
export function _renderInactiveSection(inactiveUsers, canEdit) {
  const list = Array.isArray(inactiveUsers) ? inactiveUsers : [];
  if (list.length === 0) return '';
  const rows = list.map(u => `
    <tr class="team-row-inactive">
      ${_nameCellHtml(u)}
      <td>${((Array.isArray(u.roles) && u.roles.length) ? u.roles : (u.role ? [u.role] : [])).map(r => htmlEscape(TEAM_ROLE_LABELS[r] || r)).join(', ')}</td>
      <td>${_renderLinkedBadge(u)}</td>
      ${canEdit ? `<td style="text-align:right; white-space:nowrap;">
        <button class="btn btn-secondary btn-sm" onclick="toggleTeamUserActive('${htmlEscape(u.id)}', true)" style="margin:0;">Reaktivieren</button>
        <button class="btn btn-ghost btn-sm" onclick="deleteTeamUser('${htmlEscape(u.id)}')" style="margin:0 0 0 6px; color:var(--danger);" title="Mitglied dauerhaft löschen">Löschen</button>
      </td>` : ''}
    </tr>`).join('');
  return `
    <details class="team-collapse" ${_teamState.inactiveOpen ? 'open' : ''}>
      <summary onclick="teamToggleSection('inactive')">
        Deaktivierte Mitglieder <span class="team-count">${list.length}</span>
      </summary>
      <p class="hint" style="margin:2px 0 10px; font-size:.78rem;">
        Deaktivierte Mitglieder können sich nicht anmelden und tauchen nicht in
        der Operator-Auswahl auf. ${canEdit ? 'Zum Bearbeiten zuerst reaktivieren.' : ''}
      </p>
      <table class="settings-team-table">
        <thead>
          <tr>
            <th>Name &amp; E-Mail</th>
            <th>Rolle</th>
            <th>Login</th>
            ${canEdit ? '<th></th>' : ''}
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </details>`;
}

export function _teamCardInnerHtml() {
  if (_teamState.loading) {
    return `
      <h2>Team-Mitglieder &amp; Berechtigungen</h2>
      <p class="hint">Lade Team-Liste…</p>
    `;
  }
  if (_teamState.error) {
    const err = _teamState.error;
    return `
      <h2>Team-Mitglieder &amp; Berechtigungen</h2>
      <p class="hint" style="color: var(--danger);">
        Konnte Team-Liste nicht laden: ${htmlEscape(err)}
      </p>
      <button class="btn btn-secondary btn-sm" onclick="refreshTeamCard()">Erneut laden</button>
    `;
  }
  const users = _sortTeamUsers(_teamState.users);
  const canEdit = _callerCanEditTeam(users);
  // S262: der Dienstplan-Schalter geht auch für den Manager (Spec § 6.3).
  const canSchedule = _callerCanToggleSchedule(users);
  // S133: Aktiv/Inaktiv-Split — Inaktive wandern in die Soft-Delete-Sektion.
  const { active, inactive } = _splitTeamUsers(users);

  if (!canEdit) {
    // S82-5 (S89-Übergabe): Manager bekommen eine NUR-LESE-Team-Liste statt nur
    // einen Hinweis — die phase_80-RPC liefert ihnen die Zeilen bereits. Keine
    // Invite-Form, kein Rollen-Select, keine Aktiv-Buttons (reine Anzeige). Die
    // Owner-Audit-Section bleibt Owner-only (rendert für Manager leer).
    // S262: einzige bedienbare Zelle für den Manager ist der Dienstplan-Schalter.
    const roRows = active.map(u => `
      <tr ${u.is_self ? 'class="team-row-self"' : ''}>
        ${_nameCellHtml(u)}
        <td>${((Array.isArray(u.roles) && u.roles.length) ? u.roles : (u.role ? [u.role] : [])).map(r => htmlEscape(TEAM_ROLE_LABELS[r] || r)).join(', ')}</td>
        <td>${_renderLinkedBadge(u)}</td>
        ${_renderScheduleCell(u, canSchedule)}
      </tr>`).join('');
    return `
      <h2>Team-Mitglieder &amp; Berechtigungen</h2>
      <p class="hint" style="margin-bottom: 14px;">
        Nur-Lese-Ansicht. Team-Mitglieder einladen oder Rollen ändern können nur
        Owner und Admin.${canSchedule ? ' Den Dienstplan-Schalter darfst du setzen.' : ''}
      </p>
      <table class="settings-team-table">
        <thead>
          <tr>
            <th>Name &amp; E-Mail</th>
            <th>Rolle</th>
            <th>Login</th>
            <th title="Bekommt die Verfügbarkeits-Abfrage und den Dienstplan">Dienstplan</th>
          </tr>
        </thead>
        <tbody>${roRows}</tbody>
      </table>
      ${_renderInactiveSection(inactive, false)}
    `;
  }

  const rows = active.map(u => {
    const isOwner = u.role === 'owner';
    const isSelf  = !!u.is_self;
    const disabled = isOwner || isSelf;
    // S167 Multi-Role: Rollen-Menge (Fallback: [role]). Owner/eigener Account →
    // nur Anzeige (geschützt); sonst Checkbox-Gruppe (mehrere Rollen möglich).
    const userRoles = (Array.isArray(u.roles) && u.roles.length) ? u.roles : (u.role ? [u.role] : []);
    const roleCell = disabled
      ? `<div class="team-role-readonly" title="${isSelf ? 'Eigene Rollen sind über die UI nicht änderbar.' : 'Owner-Rolle ist geschützt.'}">${userRoles.map(r => htmlEscape(TEAM_ROLE_LABELS[r] || r)).join(', ')}</div>`
      : _renderRoleCheckboxesHtml(userRoles, u.id);
    const activeBtn = disabled
      ? `<span class="hint" style="font-size:.75rem;">${isOwner ? 'geschützt' : 'du selbst'}</span>`
      : `<button class="btn btn-ghost btn-sm" onclick="toggleTeamUserActive('${htmlEscape(u.id)}', false)" style="margin:0;">Deaktivieren</button>`
        + `<button class="btn btn-ghost btn-sm" onclick="deleteTeamUser('${htmlEscape(u.id)}')" style="margin:0 0 0 6px; color:var(--danger);" title="Mitglied dauerhaft löschen">Löschen</button>`;
    return `
      <tr ${isSelf ? 'class="team-row-self"' : ''}>
        ${_nameCellHtml(u, true)}
        <td>${roleCell}</td>
        <td>${_renderLinkedBadge(u)}</td>
        <td>${_renderOperatorCell(u)}</td>
        ${_renderScheduleCell(u, canSchedule)}
        <td style="text-align:right; white-space:nowrap;">${activeBtn}</td>
      </tr>
    `;
  }).join('');

  return `
    <h2>Team-Mitglieder &amp; Berechtigungen</h2>
    <p class="hint" style="margin-bottom: 14px;">
      Lade dein Team ein. Bei der ersten Anmeldung verknüpft sich der Magic-Link
      automatisch mit dem hier angelegten Profil — eine separate Registrierung
      ist nicht nötig. Owner-Rolle ist geschützt und muss über die DB gesetzt werden.
    </p>

    <div style="margin-bottom: 18px;">
      <h3 style="font-size:.95rem; margin:0 0 8px 0;">Neues Team-Mitglied einladen</h3>
      <div class="field-row" style="gap:8px; flex-wrap:wrap;">
        <div class="field" style="flex:1; min-width:160px;">
          <label style="font-size:.8rem;">Name</label>
          <input type="text"
                 data-team-focus-key="invite-name"
                 value="${htmlEscape(_teamState.inviteName)}"
                 oninput="updateTeamInviteField('inviteName', this.value)"
                 placeholder="Max Mustermann">
        </div>
        <div class="field" style="flex:1.5; min-width:200px;">
          <label style="font-size:.8rem;">E-Mail-Adresse</label>
          <input type="email"
                 data-team-focus-key="invite-email"
                 value="${htmlEscape(_teamState.inviteEmail)}"
                 oninput="updateTeamInviteField('inviteEmail', this.value)"
                 placeholder="max@example.de">
        </div>
        <div class="field" style="flex:1; min-width:150px;">
          <label style="font-size:.8rem;">Telefon <span style="font-size:.72rem;color:var(--ink-soft);font-weight:normal;">(optional)</span></label>
          <input type="tel"
                 data-team-focus-key="invite-phone"
                 value="${htmlEscape(_teamState.invitePhone)}"
                 oninput="updateTeamInviteField('invitePhone', this.value)"
                 placeholder="+49 …">
        </div>
        <div class="field" style="flex:0 0 140px; min-width:120px;">
          <label style="font-size:.8rem;">Rolle</label>
          <select onchange="updateTeamInviteField('inviteRole', this.value)">
            ${TEAM_UI_ROLES.map(r => `<option value="${r}" ${r === _teamState.inviteRole ? 'selected' : ''}>${TEAM_ROLE_LABELS[r]}</option>`).join('')}
          </select>
        </div>
        <div class="field" style="flex:0 0 auto; align-self:flex-end;">
          <button class="btn btn-primary btn-sm" onclick="inviteTeamUser()" style="margin:0;">
            ➕ Einladen
          </button>
        </div>
      </div>
      <p class="hint" style="margin-top:6px; font-size:.78rem;">
        ${htmlEscape(TEAM_ROLE_DESCRIPTIONS[_teamState.inviteRole] || '')}
      </p>
    </div>

    <h3 style="font-size:.95rem; margin:0 0 8px 0;">Aktive Mitglieder <span class="team-count">${active.length}</span></h3>
    <table class="settings-team-table">
      <thead>
        <tr>
          <th>Name · E-Mail · Telefon</th>
          <th>Rolle</th>
          <th>Login</th>
          <th>Operator (PIN · Initialen)</th>
          <th title="Bekommt die Verfügbarkeits-Abfrage und den Dienstplan">Dienstplan</th>
          <th></th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>

    ${_renderInactiveSection(inactive, true)}
  `;
  // S167 — Änderungs-Historie hier bewusst NICHT mehr eingehängt (ausgeblendet,
  // Jonas). audit_log bleibt Single-Source in der DB.
}

/**
 * Phase 42 / S52 → S133: User-Lifecycle-Audit-Section. Nur für Owner sichtbar.
 * Eingeklapptes <details> — schaut man nur bei Bedarf an. Der Audit-Trail wird
 * erst beim ersten Aufklappen geladen (teamToggleSection → refreshTeamAuditCard).
 */
export function _renderTeamAuditSection(selfUser) {
  // S167 (Jonas) — Änderungs-Historie im Team-Tab ausgeblendet (schlankere
  // Ansicht). Die GoBD-relevanten Team-User-Mutationen werden weiterhin in
  // audit_log protokolliert → bei Bedarf in der DB nachvollziehbar. Die Funktion
  // bleibt (Audit-Logik/Tests) erhalten, wird aber im Team-Tab nicht mehr gerendert.
  if (!selfUser || selfUser.role !== 'owner') return ''; // Nur Owner

  let body;
  if (!_teamState.auditLoaded) {
    body = _teamState.auditOpen
      ? `<p class="hint">Lade Audit-Trail…</p>`
      : '';
  } else if (_teamState.auditError) {
    body = `
      <p class="hint" style="color: var(--danger);">
        Audit-Trail konnte nicht geladen werden: ${htmlEscape(_teamState.auditError)}
      </p>`;
  } else {
    const rows = Array.isArray(_teamState.audit) ? _teamState.audit : [];
    if (rows.length === 0) {
      body = `<p class="hint">Noch keine Änderungen protokolliert.</p>`;
    } else {
      const tableRows = rows.map(r => _buildTeamAuditRowHtml(r)).join('');
      body = `
        <div style="overflow-x:auto;">
          <table class="settings-team-table" style="font-size:.85rem;">
            <thead>
              <tr>
                <th>Zeitpunkt</th>
                <th>Aktor</th>
                <th>Aktion</th>
                <th>Ziel-User</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </div>`;
    }
  }

  return `
    <details class="team-collapse" ${_teamState.auditOpen ? 'open' : ''}>
      <summary onclick="teamToggleSection('audit')">Änderungs-Historie</summary>
      <p class="hint" style="margin:2px 0 10px; font-size:.78rem;">
        GoBD-relevante Team-User-Mutationen (Anlage, Rollenwechsel, (De-)Aktivierung).
        Nur für Inhaber sichtbar.
      </p>
      ${body}
    </details>
  `;
}

/**
 * Pure UI-Helper: rendert eine einzelne Audit-Zeile.
 * Exportiert für Vitest-Coverage.
 */
export function _buildTeamAuditRowHtml(entry) {
  if (!entry || !entry.id) return '';
  const when = entry.ts
    ? new Date(entry.ts).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
    : '—';
  const actorName = entry.user_name || entry.user_id || '—';
  const action    = _formatTeamAuditAction(entry.action);
  const details   = _formatTeamAuditDetails(entry.action, entry.details || {});
  const targetUser = (entry.details && (entry.details.target_name || entry.details.target_email)) || '—';
  return `
    <tr>
      <td style="white-space:nowrap;">${htmlEscape(when)}</td>
      <td>${htmlEscape(actorName)}</td>
      <td>${action}</td>
      <td>${htmlEscape(targetUser)}</td>
      <td style="color: var(--ink-soft);">${details}</td>
    </tr>
  `;
}

/**
 * Pure Helper: Action-String mit Badge.
 * Exportiert für Vitest.
 */
export function _formatTeamAuditAction(action) {
  const map = {
    'team_user.created':       { label: 'Angelegt',     bg: 'rgba(43,108,176,.12)',  fg: '#1d4f80' },
    'team_user.role_changed':  { label: 'Rolle geändert', bg: 'rgba(231,168,45,.14)', fg: '#8a6614' },
    'team_user.schedule_participant_changed': { label: 'Dienstplan', bg: 'rgba(47,107,58,.12)', fg: '#1f4a26' },
    'team_user.deactivated':   { label: 'Deaktiviert',  bg: 'rgba(192,57,43,.12)',   fg: '#a02b1f' },
    'team_user.reactivated':   { label: 'Reaktiviert',  bg: 'rgba(20,164,77,.12)',   fg: '#0e7a37' }
  };
  const cfg = map[action] || { label: action || '—', bg: 'var(--bg-soft)', fg: 'var(--ink)' };
  return `<span style="display:inline-block; padding:1px 7px; border-radius:4px; background:${cfg.bg}; color:${cfg.fg}; font-size:.76rem; white-space:nowrap;">${htmlEscape(cfg.label)}</span>`;
}

/**
 * Pure Helper: rendert Action-spezifische Details.
 * Exportiert für Vitest.
 */
export function _formatTeamAuditDetails(action, details) {
  if (!details || typeof details !== 'object') return '—';
  const t = TEAM_ROLE_LABELS;
  switch (action) {
    case 'team_user.created':
      return `Rolle: <strong>${htmlEscape(t[details.role] || details.role || '—')}</strong>`;
    case 'team_user.role_changed':
      return `<strong>${htmlEscape(t[details.role_from] || details.role_from || '?')}</strong>
              → <strong>${htmlEscape(t[details.role_to] || details.role_to || '?')}</strong>`;
    case 'team_user.deactivated':
      return `Rolle <strong>${htmlEscape(t[details.target_role] || details.target_role || '—')}</strong> deaktiviert`;
    case 'team_user.reactivated':
      return `Rolle <strong>${htmlEscape(t[details.target_role] || details.target_role || '—')}</strong> reaktiviert`;
    case 'team_user.schedule_participant_changed':
      return details.to ? 'Dienstplan-Teilnahme <strong>eingeschaltet</strong>' : 'Dienstplan-Teilnahme <strong>ausgeschaltet</strong>';
    default:
      return '—';
  }
}

/** S125 — Operator-Zelle: PIN-Status + setzen/zurücksetzen + Initialen-Feld.
 *  Nur im owner/admin-Zweig gerendert (canEdit). PIN-Wert wird NIE angezeigt
 *  (has_pin kommt aus list_team_users, der Hash bleibt server-seitig). */
export function _renderOperatorCell(u) {
  const id = htmlEscape(u.id);
  const initials = htmlEscape(u.display_initials || '');
  const hasPin = u.has_pin === true;
  // S133 (Jonas-Befund): PIN als Kästchen analog zum Initialen-Input statt
  // Badge+Button-Reihe. Klick aufs Kästchen öffnet das PIN-Pad-Modal
  // (setzen/ändern); bei gesetzter PIN gibt's ein dezentes ✕ zum Zurücksetzen.
  // S133 (Jonas-Befund): PIN als Kästchen analog zum Initialen-Input. Klick aufs
  // Kästchen öffnet das PIN-Pad-Modal (setzen/ändern); bei gesetzter PIN klebt ein
  // dezentes ✕ (Reset) direkt rechts an der Box (S209: eigene .team-pin-wrap-Gruppe,
  // damit das ✕ nicht mehr unter die Box umbricht).
  const pinBox = hasPin
    ? `<span class="team-pin-wrap">
         <button type="button" class="team-pin-box team-pin-set" onclick="setTeamUserPin('${id}')"
                 title="PIN gesetzt — klicken zum Ändern">••••</button>
         <button type="button" class="team-pin-reset" onclick="resetTeamUserPin('${id}')"
                 title="PIN zurücksetzen" aria-label="PIN zurücksetzen">✕</button>
       </span>`
    : `<button type="button" class="team-pin-box team-pin-empty" onclick="setTeamUserPin('${id}')"
               title="Noch keine PIN — klicken zum Setzen">PIN</button>`;
  // S126 Befund 1+2: einzeilig; Initialen Auto-Save on change (kein Button,
  // kein Label — steht im Spaltenkopf), dezenter Platzhalter „AB".
  return `
    <div class="team-op-cell">
      <input type="text" maxlength="3" placeholder="AB" value="${initials}"
             data-team-focus-key="initials-${id}" data-initials-for="${id}"
             onchange="saveTeamInitials('${id}')"
             title="Initialen (1–3 Zeichen) — wird automatisch gespeichert"
             class="team-initials-input">
      ${pinBox}
    </div>
  `;
}

/** S209 — Telefon: generisches optionales Kontaktfeld, DEZENT unter der Mail
 *  (sieht aus wie die Mail-Zeile, ist aber inline editierbar; Auto-Save on change,
 *  set_team_user_phone). Kein Fahrer-Bezug. */
export function _renderPhoneCell(u) {
  const id = htmlEscape(u.id);
  const phone = htmlEscape(u.phone || '');
  return `
    <input type="tel" placeholder="+ Telefon" value="${phone}"
           data-team-focus-key="phone-${id}" data-phone-for="${id}"
           onchange="saveTeamPhone('${id}')"
           title="Telefon (optional) — wird automatisch gespeichert"
           class="team-phone-input">
  `;
}

/** Action: Initialen speichern (set_staff_initials, auditiert). Liest den Wert
 *  direkt aus dem Input (kein Buffer-State, Memory feedback_gg_template_autosave_trailing_race).
 *  Leerer Wert = zurücksetzen. Auto-Save on change (Blur/Enter). */
export async function saveTeamInitials(userId) {
  const el = document.querySelector(`input[data-initials-for="${userId}"]`);
  const value = el ? (el.value || '').trim() : '';
  if (value.length > 3) { showToast('Initialen dürfen höchstens 3 Zeichen haben.', 'error'); return; }
  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { error } = await supa.rpc('set_staff_initials', { p_user_id: userId, p_initials: value });
    if (error) throw error;
    showToast(value ? `Initialen gesetzt: ${value}` : 'Initialen zurückgesetzt.', 'success');
    await refreshTeamCard();
  } catch (err) {
    console.error('[team] saveTeamInitials:', err);
    showToast('Initialen speichern fehlgeschlagen: ' + (err.message || err), 'error');
  }
}

/** S209 — Cockpit-Dropdown der Fahrer frisch halten: nach jeder Team-Änderung,
 *  die die Fahrer-Menge oder deren Telefon betreffen könnte, das im Liefer-
 *  Cockpit (delivery.js) gelauschte Event feuern. Fehlertolerant. */
function _emitDriversChanged() {
  try {
    if (window.App && window.App.events && typeof window.App.events.emit === 'function') {
      window.App.events.emit('drivers:changed');
    }
  } catch (_) { /* ignore */ }
}

/** S209 Action: Telefon eines Team-Users speichern (set_team_user_phone,
 *  auditiert). Liest den Wert direkt aus dem Input; leerer Wert = NULL.
 *  Auto-Save on change. */
export async function saveTeamPhone(userId) {
  const el = document.querySelector(`input[data-phone-for="${userId}"]`);
  const value = el ? (el.value || '').trim() : '';
  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { error } = await supa.rpc('set_team_user_phone', { p_user_id: userId, p_phone: value });
    if (error) throw error;
    showToast(value ? `Telefon gespeichert: ${value}` : 'Telefon entfernt.', 'success');
    await refreshTeamCard();
    _emitDriversChanged();
  } catch (err) {
    console.error('[team] saveTeamPhone:', err);
    showToast('Telefon speichern fehlgeschlagen: ' + (err.message || err), 'error');
  }
}

/** Action: PIN setzen/ändern über das stylebare PIN-Pad-Modal (S126 Befund 3).
 *  4 Ziffern, nie im Klartext gespeichert. */
export function setTeamUserPin(userId) {
  const u = (_teamState.users || []).find(x => x.id === userId);
  const name = u && u.name ? u.name : '';
  openSetPinModal({
    title: 'PIN festlegen',
    subtitle: name ? `Für ${name}` : '',
    onComplete: async (pin) => {
      if (!/^[0-9]{4}$/.test(pin)) return { ok: false, message: 'Die PIN muss aus genau 4 Ziffern bestehen.' };
      try {
        const supa = window.App && window.App.supabase;
        if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
        const { error } = await supa.rpc('set_staff_pin', { p_user_id: userId, p_pin: pin });
        if (error) throw error;
        showToast('PIN gesetzt.', 'success');
        await refreshTeamCard();
        return { ok: true };
      } catch (err) {
        console.error('[team] setTeamUserPin:', err);
        return { ok: false, message: 'PIN setzen fehlgeschlagen: ' + (err.message || err) };
      }
    }
  });
}

/** Action: PIN zurücksetzen (set_staff_pin mit leerer PIN = Hash auf NULL). */
export async function resetTeamUserPin(userId) {
  if (!(await window.styledConfirm({ title: 'PIN dieses Mitarbeiters zurücksetzen?', body: 'Er kann sich dann erst nach Neuvergabe wieder anmelden.', danger: true, confirmLabel: 'Zurücksetzen' })).confirmed) return;
  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { error } = await supa.rpc('set_staff_pin', { p_user_id: userId, p_pin: '' });
    if (error) throw error;
    showToast('PIN zurückgesetzt.', 'success');
    await refreshTeamCard();
  } catch (err) {
    console.error('[team] resetTeamUserPin:', err);
    showToast('PIN zurücksetzen fehlgeschlagen: ' + (err.message || err), 'error');
  }
}

/** Input-Handler — schreibt nur in Module-State, kein DOM-Reset, kein Server. */
export function updateTeamInviteField(key, value) {
  if (key !== 'inviteName' && key !== 'inviteEmail' && key !== 'invitePhone' && key !== 'inviteRole') return;
  _teamState[key] = value;
  // Bei Rollen-Wechsel beschreiben wir den Hint-Text via Re-Render.
  if (key === 'inviteRole') _renderTeamCardInner();
}

/** Action-Handler: Invite. Validiert lokal + ruft RPC + zeigt Toast +
 *  reload't die Liste bei Erfolg. */
export async function inviteTeamUser() {
  const name  = (_teamState.inviteName || '').trim();
  const email = (_teamState.inviteEmail || '').trim();
  const phone = (_teamState.invitePhone || '').trim();
  const role  = _teamState.inviteRole;

  if (!name) {
    showToast('Bitte einen Namen angeben.', 'error');
    return;
  }
  if (!_isValidTeamEmail(email)) {
    showToast('Bitte eine gültige E-Mail-Adresse angeben.', 'error');
    return;
  }
  if (!_isUiAssignableRole(role)) {
    showToast('Diese Rolle kann nicht über die UI vergeben werden.', 'error');
    return;
  }

  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { error } = await supa.rpc('create_team_user', {
      p_email: email,
      p_name: name,
      p_role: role,
      p_phone: phone || null
    });
    if (error) throw error;
    showToast(`${name} eingeladen. Der erste Magic-Link-Login verknüpft das Profil automatisch.`, 'success');
    // Form zurücksetzen, dann Liste neu laden.
    _teamState.inviteName = '';
    _teamState.inviteEmail = '';
    _teamState.invitePhone = '';
    // S133: Reset auf den Form-Default 'service' (war 'staff' — S131-Rest).
    _teamState.inviteRole = 'service';
    await refreshTeamCard();
    _emitDriversChanged();
  } catch (err) {
    console.error('[team] inviteTeamUser:', err);
    const msg = (err && err.message) || String(err);
    showToast('Einladung fehlgeschlagen: ' + msg, 'error');
  }
}

/** Action-Handler: Rolle ändern. */
export async function changeTeamUserRole(userId, newRole) {
  if (!_isUiAssignableRole(newRole)) {
    showToast('Diese Rolle kann nicht über die UI vergeben werden.', 'error');
    await refreshTeamCard();
    return;
  }
  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { error } = await supa.rpc('update_team_user_role', {
      p_user_id: userId,
      p_new_role: newRole
    });
    if (error) throw error;
    showToast(`Rolle aktualisiert: ${TEAM_ROLE_LABELS[newRole] || newRole}.`, 'success');
    await refreshTeamCard();
  } catch (err) {
    console.error('[team] changeTeamUserRole:', err);
    showToast('Rollen-Update fehlgeschlagen: ' + (err.message || err), 'error');
    await refreshTeamCard();
  }
}

/** Action-Handler (S167 Multi-Role): die volle Rollen-Menge eines Users setzen.
 *  Sammelt alle angehakten Checkboxen der Gruppe und ruft update_team_user_roles.
 *  Mind. eine Rolle Pflicht (sonst Revert). */
export async function changeTeamUserRoles(userId, checkboxEl) {
  const container = (checkboxEl && checkboxEl.closest) ? checkboxEl.closest('.team-role-checks') : null;
  const roles = container
    ? Array.from(container.querySelectorAll('input[type="checkbox"]:checked')).map(c => c.value)
    : [];
  if (roles.length === 0) {
    if (checkboxEl) checkboxEl.checked = true; // letzte Rolle bleibt erhalten
    showToast('Mindestens eine Rolle ist erforderlich.', 'info');
    return;
  }
  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { error } = await supa.rpc('update_team_user_roles', { p_user_id: userId, p_roles: roles });
    if (error) throw error;
    showToast('Rollen aktualisiert.', 'success');
    await refreshTeamCard();
    _emitDriversChanged();
  } catch (err) {
    console.error('[team] changeTeamUserRoles:', err);
    showToast('Rollen-Update fehlgeschlagen: ' + (err.message || err), 'error');
    await refreshTeamCard();
  }
}

/** Action-Handler (S167): Mitglied DAUERHAFT löschen (Hard-Delete, kein Soft-
 *  Delete). Owner/eigener Account sind server-fest geschützt (delete_team_user,
 *  phase_127). Doppelte Bestätigung gegen Fehlklick; audit_log behält den Eintrag
 *  (GoBD), die operativen Verweise (devices/operator_sessions) werden DB-seitig
 *  bereinigt, der Auth-User (E-Mail) wird freigegeben. */
export async function deleteTeamUser(userId) {
  if (!(await window.styledConfirm({
    title: 'Dieses Mitglied DAUERHAFT löschen?',
    body: 'Der Account wird komplett entfernt (nicht nur deaktiviert) und die E-Mail wird wieder frei. Das kann nicht rückgängig gemacht werden.',
    danger: true, confirmLabel: 'Dauerhaft löschen'
  })).confirmed) {
    return;
  }
  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { error } = await supa.rpc('delete_team_user', { p_user_id: userId });
    if (error) throw error;
    showToast('Mitglied dauerhaft gelöscht.', 'success');
    await refreshTeamCard();
    _emitDriversChanged();
  } catch (err) {
    console.error('[team] deleteTeamUser:', err);
    showToast('Löschen fehlgeschlagen: ' + (err.message || err), 'error');
    await refreshTeamCard();
  }
}

/** Action-Handler: (De-)Aktivieren. */
export async function toggleTeamUserActive(userId, nextActive) {
  const verb = nextActive ? 'reaktivieren' : 'deaktivieren';
  if (!(await window.styledConfirm({ title: `Diesen Nutzer ${verb}?`, confirmLabel: nextActive ? 'Reaktivieren' : 'Deaktivieren' })).confirmed) {
    return;
  }
  try {
    const supa = window.App && window.App.supabase;
    if (!supa) throw new Error('Supabase-Client nicht verfügbar.');
    const { error } = await supa.rpc('set_team_user_active', {
      p_user_id: userId,
      p_active: !!nextActive
    });
    if (error) throw error;
    showToast(nextActive ? 'Nutzer reaktiviert.' : 'Nutzer deaktiviert.', 'success');
    await refreshTeamCard();
    _emitDriversChanged();
  } catch (err) {
    console.error('[team] toggleTeamUserActive:', err);
    showToast('(De)Aktivierung fehlgeschlagen: ' + (err.message || err), 'error');
    await refreshTeamCard();
  }
}

// -----------------------------------------------------------------------------
// Side-Effect: window.* für Inline-Handler im Settings-HTML.
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    refreshTeamCard,
    refreshTeamAuditCard,
    updateTeamInviteField,
    inviteTeamUser,
    changeTeamUserRole,
    changeTeamUserRoles,
    toggleTeamUserActive,
    deleteTeamUser,
    saveTeamInitials,
    saveTeamPhone,
    setTeamUserPin,
    resetTeamUserPin,
    teamToggleSection,
    // S222 Cut 2 — Rollen-Idle-Timeouts (Team-Tab-Karte)
    updateOperatorRoleTimeout,
    // S262 Cut A — Dienstplan-Schalter je User + Zyklus-Karte
    toggleTeamUserSchedule,
    updateScheduleCycleField
  });
}
